import { useToast } from '@chakra-ui/react';
import { IconDownload } from '@tabler/icons';
import { UseQueryResult } from '@tanstack/react-query';
import { ButtonComponent } from 'components/buttons/ButtonComponent';
import { useGlobalContext } from 'contexts/GlobalContext';
import { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { mapErrorToToastProps } from 'utils/forms/mapErrorToToastProps';

import { useExport as useExportGeneric } from './hooks/useExport';
import { useExportSlow as useExportSlowGeneric } from './hooks/useExportSlow';
import { ExportButtonMessages } from './variables/exportButtonMessages';

export type Module = 'hierarquiaComercial' | 'produto' | 'clienteRelacionado';

interface IButtonComponentGenericProps {
	onClick: () => void;
	isDisabled: boolean;
	isLoading: boolean;
}
const ButtonComponentGeneric = ({
	onClick,
	isDisabled,
}: IButtonComponentGenericProps) => {
	return (
		<ButtonComponent
			data-test='button-exportar'
			iconType={<IconDownload />}
			type='icon'
			onClick={onClick}
			disabled={isDisabled}
		/>
	);
};
export type IUseExport = <Params>(
	module: Module,
	params: Params | undefined
) =>
	| UseQueryResult<any, any>
	| {
			refetch: () => Promise<void>;
			data: any;
			isFetching: boolean;
	  };
interface IExportGenericCSVButtonProps<Params> {
	useExport?: IUseExport;
	useExportSlow?: IUseExport;
	module: Module;
	errorDescription?: string;
	params?: Params | undefined;
	isDisabled?: boolean;
	Component?: (props: IButtonComponentGenericProps) => JSX.Element;
	agGrid?: boolean;
	exportAll?: boolean;
	nameExport: string;
	showLoading?: boolean;
}
export default function ExportGenericCSVButtons<Params>(
	props: IExportGenericCSVButtonProps<Params>
) {
	const {
		module,
		errorDescription = 'Não há dados para exportar!',
		isDisabled = false,
		Component = ButtonComponentGeneric,
		params,
		agGrid = false,
		useExport = useExportGeneric,
		nameExport,
		useExportSlow = useExportSlowGeneric,
		exportAll = true,
	} = props;

	const { pathCompanyName } = useGlobalContext();
	const [dataCSV, setDataCSV] = useState([]);
	const csvInstance = useRef<any | null>(null);
	const { refetch: refetchIsSlow } = useExportSlow<Params>(module, params);

	const useExp = useExport<Params>(module, params);
	const toast = useToast();
	const getDataCsv = () => {
		return dataCSV;
	};

	useEffect(() => {
		if (useExp && useExp?.data) {
			setDataCSV(useExp?.data);
		}
	}, [useExp]);
	const CSVFileName = (): string => {
		const fileNameDefault = `easy360_${pathCompanyName}`;
		return `${fileNameDefault}_${nameExport}.csv`;
	};

	const notFoundData = () => {
		toast({
			title: 'Atenção! ',
			description: errorDescription,
			status: 'warning',
			position: 'bottom-left',
			duration: 8000,
			isClosable: true,
		});
	};
	return (
		<span style={{ position: 'relative', display: 'flex' }}>
			<Component
				onClick={() => {
					// simular click no botão de download
					if (
						csvInstance.current &&
						csvInstance.current.link &&
						useExp
					) {
						if (agGrid && exportAll) {
							refetchIsSlow().then(data => {
								const isSlow = data['data'];
								if (isSlow) {
									toast({
										title: ExportButtonMessages.MSG_TITLE_AG_GRID_START_EXPORT,
										description:
											ExportButtonMessages.MSG_TEXT_AG_GRID_START_EXPORT,
										status: 'warning',
										position: 'bottom-left',
										duration: 8000,
										isClosable: true,
									});
								}
							});
						}
						useExp
							.refetch()
							.then(data => {
								if (!exportAll || !!data['data']?.length) {
									toast({
										title: 'Sucesso!',
										description:
											ExportButtonMessages.MSG_EXPORT_SUCCESS,
										status: 'success',
										duration: 8000,
										isClosable: true,
										position: 'bottom-left',
									});
								}

								if (exportAll && !!data['data']?.length) {
									setDataCSV(data['data']);
									csvInstance.current.link.click();
								} else if (exportAll) {
									notFoundData();
								}
							})
							.catch(error => {
								if (!exportAll || error.status === 404) {
									notFoundData();
								} else {
									const toastErrors: Array<any> =
										mapErrorToToastProps(
											error,
											'Erro ao exportar!'
										);
									toastErrors?.forEach(element =>
										toast(element)
									);
								}
							});
					}
				}}
				isDisabled={isDisabled || useExp?.isFetching}
				isLoading={useExp?.isFetching}
			/>
			<CSVLink
				data={getDataCsv()}
				style={{ display: 'none' }}
				ref={csvInstance}
				filename={CSVFileName()}
			>
				<ButtonComponent
					iconType={<IconDownload />}
					type='icon'
				/>
			</CSVLink>
		</span>
	);
}
